import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  TablePagination,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import Can from '../../components/permissions/Can';
import api from '../../api/config';
import { CUSTOMERS } from '../../api/endpoints';
import { extractList, extractPagination } from '../../utils/response';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/useAuth';
import SweetAlert from '../../utils/sweetAlert';
import {
  CUSTOMERS_CREATE_PATH,
  CUSTOMERS_EDIT_PATH,
  CUSTOMERS_VIEW_PATH,
} from '../../navigations/path';
import {
  CUSTOMER_PERMISSIONS,
  CUSTOMER_LIST_ACCESS,
} from './customerPermissions';
import {
  formatGender,
  getCustomerStatusLabel,
  getCustomerTeamsLabel,
} from './customerApiHelpers';

const CustomerList = () => {
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission } = useAuth();
  const canViewList = hasAnyPermission(CUSTOMER_LIST_ACCESS);
  const canEdit = hasPermission(CUSTOMER_PERMISSIONS.EDIT);
  const canView = hasPermission(CUSTOMER_PERMISSIONS.VIEW);
  const canDelete = hasPermission(CUSTOMER_PERMISSIONS.DELETE);
  const canCreate = hasPermission(CUSTOMER_PERMISSIONS.CREATE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch]);

  const fetchCustomers = useCallback(async () => {
    if (!canViewList) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = {
        'relations[]': ['status'],
        paginate: 1,
        page: page + 1,
        per_page: rowsPerPage,
      };
      const q = debouncedSearch.trim();
      if (q) params.search = q;

      const { data } = await api.get(CUSTOMERS.LIST, { params });
      const list = extractList(data);
      const pag = extractPagination(data);
      setRows(Array.isArray(list) ? list : []);
      setTotal(Number(pag.total) || 0);
      setError(null);
    } catch (err) {
      console.error('Fetch customers error:', err);
      setError('Failed to load customers.');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canViewList, page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    if (!canViewList) {
      toast.error('You do not have permission to view customers.');
      return;
    }
    fetchCustomers();
  }, [canViewList, fetchCustomers]);

  const openActionMenu = (event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;
    const target = rows.find((r) => r.id === id);
    const result = await SweetAlert.deleteConfirm(target?.name || 'This Customer');
    if (!result.isConfirmed) return;
    try {
      await api.delete(CUSTOMERS.BY_ID(id));
      await SweetAlert.success({ text: 'Customer deleted successfully.' });
      await fetchCustomers();
    } catch (err) {
      await SweetAlert.error({
        text: err.response?.data?.message || 'Failed to delete customer',
      });
    }
  };

  const getStatusChip = (statusName) => {
    const normalized = (statusName || '').toLowerCase();
    const active = normalized === 'active';
    return (
      <Chip
        label={statusName || '—'}
        color={active ? 'success' : 'default'}
        size="small"
        sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
      />
    );
  };

  return (
    <PageLayout>
      <Box sx={{ flexGrow: 1, p: 4, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Customers ({total})
          </Typography>
          {canCreate && (
            <Can permission={CUSTOMER_PERMISSIONS.CREATE}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(CUSTOMERS_CREATE_PATH)}
                disabled={loading}
              >
                Add Customer
              </Button>
            </Can>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            mb: 3,
            flexWrap: 'wrap',
            alignItems: 'center',
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <TextField
            label="Search name, email, or username"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 280 }}
            disabled={loading}
          />
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" align="center" sx={{ my: 4 }}>
            {error}
          </Typography>
        )}
        {!loading && !error && total === 0 && (
          <Typography align="center" sx={{ my: 4 }}>
            No customers found.
          </Typography>
        )}

        {!loading && !error && total > 0 && (
          <Paper elevation={1} sx={{ borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: '65vh' }}>
              <Table stickyHeader aria-label="customers table">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'primary.main',
                      '& th': { color: 'primary.contrastText', fontWeight: 'bold' },
                    }}
                  >
                    <TableCell>Name</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Teams</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name || '—'}</TableCell>
                      <TableCell>{row.username || '—'}</TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>{row.phone || '—'}</TableCell>
                      <TableCell>{getCustomerTeamsLabel(row)}</TableCell>
                      <TableCell>{getStatusChip(getCustomerStatusLabel(row))}</TableCell>
                      <TableCell>{formatGender(row.gender)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="Actions"
                          onClick={(e) => openActionMenu(e, row)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Menu
              anchorEl={actionMenuAnchor}
              open={Boolean(actionMenuAnchor)}
              onClose={closeActionMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { minWidth: 140 } }}
            >
              {canView && (
                <MenuItem
                  onClick={() => {
                    if (actionMenuRow) navigate(`${CUSTOMERS_VIEW_PATH}/${actionMenuRow.id}`);
                    closeActionMenu();
                  }}
                >
                  <ListItemIcon>
                    <VisibilityIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>View</ListItemText>
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem
                  onClick={() => {
                    if (actionMenuRow) navigate(`${CUSTOMERS_EDIT_PATH}/${actionMenuRow.id}`);
                    closeActionMenu();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  onClick={() => {
                    if (actionMenuRow) handleDelete(actionMenuRow.id);
                    closeActionMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              )}
            </Menu>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        )}
      </Box>
    </PageLayout>
  );
};

export default CustomerList;
